import type {
  AIInstructionDraft,
  CustomerRow,
  ProjectUserIdentifierRow,
  TicketDetailTicket,
  TicketSdAdminApiAction,
  TicketSdAdminApiActionResponse,
  TicketSdAdminEmailChoiceDetail,
} from '@/types/api'
import { visibleCustomerIdentifiers } from '@/lib/identifier-labels'

export const SD_ADMIN_API_ACTIONS: Array<{ key: TicketSdAdminApiAction; label: string }> = [
  { key: 'payment_link', label: 'Платежная ссылка' },
  { key: 'otp_code', label: 'OTP код' },
  { key: 'paid_until', label: 'Дата окончания' },
  { key: 'keys_count', label: 'Кол-во ключей' },
  { key: 'devices_count', label: 'Кол-во устройств' },
]

export function getSdAdminEmailChoice(detail: unknown): string[] | null {
  if (!detail || typeof detail !== 'object') return null
  const choice = detail as Partial<TicketSdAdminEmailChoiceDetail>
  if (choice.code !== 'sd_admin_email_choice_required' || !Array.isArray(choice.email_options)) {
    return null
  }
  const emails = choice.email_options
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value): value is string => value.length > 0)
  return emails.length > 1 ? emails : null
}

export function hasSdAdminValue(result: TicketSdAdminApiActionResponse): boolean {
  return result.value !== null && result.value !== undefined && result.value !== ''
}

export function sdAdminFinalText(result: TicketSdAdminApiActionResponse): string {
  return (result.outbound_text || result.message_text || result.display_text || '').trim()
}

export const OPEN_HEARTBEAT_MS = 45_000
/**
 * Fallback-опрос ленты, только если WebSocket карточки не подключён.
 * При живом WS новые сообщения приходят по сокету — полный ticketDetail каждые 3с
 * душит API при нескольких открытых вкладках.
 */
export const THREAD_POLL_MS = 15_000
/** Сколько шаблонов/макросов в меню по «/» (как MAX_FREQUENT в classic TicketDetail). */
export const REPLY_SLASH_MAX_ITEMS = 5
const TICKET_REPLY_DRAFTS_KEY = 'sd2_ticket_reply_drafts_v1'
const TICKET_REPLY_DRAFTS_MAX = 100

type TicketReplyDraftsStore = Record<string, { text: string; updated_at: number }>

export function readTicketReplyDraft(ticketId: string): string {
  try {
    const raw = localStorage.getItem(TICKET_REPLY_DRAFTS_KEY)
    if (!raw) return ''
    const parsed = JSON.parse(raw) as TicketReplyDraftsStore
    const item = parsed?.[ticketId]
    return typeof item?.text === 'string' ? item.text : ''
  } catch {
    return ''
  }
}

export function writeTicketReplyDraft(ticketId: string, text: string): void {
  try {
    const raw = localStorage.getItem(TICKET_REPLY_DRAFTS_KEY)
    const parsed = raw ? (JSON.parse(raw) as TicketReplyDraftsStore) : {}
    const next: TicketReplyDraftsStore = parsed && typeof parsed === 'object' ? { ...parsed } : {}
    next[ticketId] = { text, updated_at: Date.now() }
    const entries = Object.entries(next)
      .sort((a, b) => (b[1]?.updated_at || 0) - (a[1]?.updated_at || 0))
      .slice(0, TICKET_REPLY_DRAFTS_MAX)
    localStorage.setItem(TICKET_REPLY_DRAFTS_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    /* ignore */
  }
}

export function removeTicketReplyDraft(ticketId: string): void {
  try {
    const raw = localStorage.getItem(TICKET_REPLY_DRAFTS_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as TicketReplyDraftsStore
    if (!parsed || typeof parsed !== 'object' || !(ticketId in parsed)) return
    delete parsed[ticketId]
    localStorage.setItem(TICKET_REPLY_DRAFTS_KEY, JSON.stringify(parsed))
  } catch {
    /* ignore */
  }
}

export type ReplySlashItem = {
  type: 'template' | 'macro'
  id: string
  name: string
  /** Без ведущего / — для фильтра и подписи. */
  shortcut: string
  content: string
}

export type SlashMenuUi = {
  filtered: ReplySlashItem[]
  selectedIndex: number
  left: number
  bottom: number
}

/** Верхняя граница для расчёта минимальной высоты карточки «Ответ» (подсказка, не жёсткий max блока). */
export const REPLY_PANEL_H_MAX = 560
export const REPLY_PANEL_LS_COLLAPSED = 'sd2_ticket_reply_collapsed'
export const SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY = 'sd2_show_assignment_system_messages'

export const AI_INSTRUCTION_PRIORITY_OPTIONS = [
  { value: 'normal', label: 'normal' },
  { value: 'low', label: 'low' },
  { value: 'high', label: 'high' },
  { value: 'urgent', label: 'urgent' },
] as const

export type AiInstructionFormState = {
  category: string
  priority: string
  triggers: string
  action: string
  response_from_db: string
  data_collected: string
  questions_asked: string
  tags: string
  when_transfer_to_operator: string
  message_to_client_on_transfer: string
}

export function emptyAiInstructionForm(): AiInstructionFormState {
  return {
    category: '',
    priority: 'normal',
    triggers: '',
    action: '',
    response_from_db: '',
    data_collected: '',
    questions_asked: '',
    tags: '',
    when_transfer_to_operator: '',
    message_to_client_on_transfer: '',
  }
}

export function draftToAiInstructionForm(d: AIInstructionDraft | null | undefined): AiInstructionFormState {
  const base = emptyAiInstructionForm()
  if (!d) return base
  const pr = String(d.priority || 'normal').toLowerCase()
  return {
    ...base,
    category: d.category || '',
    priority: ['low', 'normal', 'high', 'urgent'].includes(pr) ? pr : 'normal',
    triggers: d.triggers || '',
    action: d.action || '',
    response_from_db: d.response_from_db || '',
    data_collected: d.data_collected || '',
    questions_asked: d.questions_asked || '',
    tags: d.tags || '',
    when_transfer_to_operator: d.when_transfer_to_operator || '',
    message_to_client_on_transfer: d.message_to_client_on_transfer || '',
  }
}

export function readReplyPanelCollapsed(): boolean {
  try {
    return localStorage.getItem(REPLY_PANEL_LS_COLLAPSED) === '1'
  } catch {
    return false
  }
}

/** По умолчанию системные сообщения о назначении скрыты. */
export function readShowAssignmentSystemMessages(): boolean {
  try {
    return localStorage.getItem(SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY) === '1'
  } catch {
    return false
  }
}
export function parseTicketRef(raw: string | undefined): string {
  if (!raw) return ''
  return raw.replace(/-tags$/i, '')
}

export type DisplayIdent = {
  identifier_type: string
  identifier_value: string
  id?: string
  display_name?: string | null
}

/** Статусы в селекте карточки (без «На паузе» / «Спам»). */
export const TICKET_DETAIL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Новое' },
  { value: 'open', label: 'Открыто' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'emergency', label: 'Авария' },
  { value: 'resolved', label: 'Решено' },
  { value: 'closed', label: 'Закрыто' },
]

export const TICKET_DETAIL_PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]

export function mergeCustomerIdentifiers(
  ticket: TicketDetailTicket | null,
  _cust: CustomerRow | null,
  rows: ProjectUserIdentifierRow[],
): DisplayIdent[] {
  if (!ticket?.customer_id || !ticket.project_id) return []
  const allIdentifiers = rows.map((r) => ({
    id: r.id,
    identifier_type: (r.identifier_type || '').toString().trim().toLowerCase(),
    identifier_value: (r.identifier_value || '').toString().trim(),
    display_name: r.display_name ?? null,
  }))
  const seenCombinations = new Set<string>()
  const uniqueIdentifiers: DisplayIdent[] = []
  for (const ident of visibleCustomerIdentifiers(allIdentifiers)) {
    const key = `${ident.identifier_type}:${(ident.identifier_value || '').trim().toLowerCase()}`
    if (!seenCombinations.has(key)) {
      seenCombinations.add(key)
      uniqueIdentifiers.push(ident)
    }
  }
  const telegramTypes = ['telegram_id', 'telegram_username']
  const telegram = uniqueIdentifiers.filter((i) => telegramTypes.includes(i.identifier_type))
  const rest = uniqueIdentifiers.filter((i) => !telegramTypes.includes(i.identifier_type))
  return [...telegram, ...rest]
}
